import React from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet } from 'react-native';
import { View } from 'react-native';
import {
  BodyText,
  SectionHeader,
  theme,
  useResponsive,
  VStack,
} from '@sg/garnish';

import { CheckoutLayoutBlock } from '@order/components';
import { DurationUnit } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import { SweetpassCheckoutCta } from '..';
import { SweetpassCheckoutCheckbox } from '../SweetpassCheckoutCheckbox';
import { SweetpassCheckoutPrice } from '../SweetpassCheckoutPrice';

export const SweetpassCheckoutBlock = (props: SweetpassCheckoutBlockProps) => {
  const {
    testID,
    termsAndConditions,
    cta,
    planPrice,
    billingFrequencyUnit = DurationUnit.Month,
    hasFreeTrial,
    isLoading,
    hasAcceptedTerms,
    canCheckout,
    handleAcceptTerms,
    handleCta,
  } = props;

  const { t } = useLocalizationContext();
  const { match } = useResponsive();

  const checkoutPriceStyles = [
    styles.lineItem,
    match([styles.lineItemXS, styles.lineItemSM]),
  ];

  const customStackSpacing = match([theme.spacing['10'], theme.spacing['8']]);

  return (
    <CheckoutLayoutBlock testID={testID}>
      <SectionHeader heading={t('sweetpass.checkout.your-purchase')} />

      <VStack customSpacing={customStackSpacing}>
        <View style={checkoutPriceStyles}>
          <BodyText bold>{t('sweetpass-plus')}</BodyText>

          <SweetpassCheckoutPrice
            planPrice={planPrice}
            billingFrequencyUnit={billingFrequencyUnit}
            hasFreeTrial={hasFreeTrial}
          />
        </View>
        <SweetpassCheckoutCheckbox
          termsAndConditions={termsAndConditions}
          hasFreeTrial={hasFreeTrial}
          hasAcceptedTerms={hasAcceptedTerms}
          handleAcceptTerms={handleAcceptTerms}
        />
      </VStack>

      <View style={styles.footer}>
        <SweetpassCheckoutCta
          testID="sweetpass.checkout.cta-desktop"
          isLoading={isLoading}
          disabled={!canCheckout}
          cta={cta}
          onPress={handleCta}
        />
      </View>
    </CheckoutLayoutBlock>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type SweetpassCheckoutBlockProps = Readonly<{
  termsAndConditions: string;
  cta: string;
  planPrice: number;
  hasFreeTrial: boolean;
  billingFrequencyUnit?: DurationUnit;
  isLoading: boolean;
  hasAcceptedTerms: boolean;
  canCheckout: boolean;
  handleAcceptTerms: (hasAcceptedTerms: boolean) => void;
  handleCta: () => void;
}> &
  Pick<ViewProps, 'testID'>;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  lineItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.TEXT_COLOR,
  },
  lineItemXS: {
    paddingVertical: theme.spacing['6'],
  },
  lineItemSM: {
    paddingVertical: theme.spacing['10'],
  },
  footer: {
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
});
